import {
  FilterComponentData,
  FilterPillComponentData,
  FilterReducerState,
  IdValuePair,
  SelectFilterFreetextOptionPayload,
  SelectFilterSelectOptionPayload,
  ToggleFreetextFilterPayload,
} from './GenericFiltersReducer.d'

export const extractPillComponentData = (
  ComponentDataIn: FilterComponentData,
  value: string | IdValuePair,
): FilterPillComponentData => {
  const { id, labelText, type } = ComponentDataIn
  return { id, parentId: id, labelText, type, value } as FilterPillComponentData
}

export const getInitialOrderedFilters = (filters: FilterComponentData[]) => {
  const returnArray: FilterPillComponentData[] = []
  filters.forEach(filterObject => {
    const { activeFilters } = filterObject
    activeFilters.forEach(activeFilter => {
      returnArray.push(extractPillComponentData(filterObject, activeFilter) as FilterPillComponentData)
    })
  })
  return returnArray
}

export const setUrlSearchParamsFromSelectObject = (url: URLSearchParams, id: string, filters: IdValuePair[]) => {
  url.set(id, filters.map(({ id }) => id).join(','))
}

export const setUrlSearchParamsFromFreetextObject = (url: URLSearchParams, id: string, filters: string[]) => {
  url.set(id, filters.join(','))
}

export function deepCopyFilterObjects<T>(filterObjects: T[]): T[] {
  return JSON.parse(JSON.stringify(filterObjects)) as T[]
}
export function deepCopyFilterObject<T>(filterObjects: T): T {
  return JSON.parse(JSON.stringify(filterObjects)) as T
}

/**
 * Used to get deep copies of the common data that is featured in the majority of the reducer functions
 *
 * @param state - The filter reducer state
 * @returns deep copied index, orderedFilters and urlParams data from the reducer state
 */
export const getDeepCopiedData = (state: FilterReducerState) => {
  const newIndex = deepCopyFilterObject(state.index)
  const newOrderedFilters = deepCopyFilterObjects(state.orderedFilters)
  const newUrl = new URLSearchParams(state.urlQueryParams)
  return { newIndex, newUrl, newOrderedFilters }
}

export const getDoesFilterObjectExist = (key: string, filterObjects: Record<string, FilterComponentData>) =>
  Reflect.has(filterObjects, key)

/**
 * Used to check if a filter value already exists in the ordered filters array
 *
 * @param orderedFilters - The ordered filters array from the reducer state, containing the existing filter selections
 * @param filterEntityId - The id of the relevant filter entity (e.g. 'clients' or 'brands')
 * @param filterValueId - The id of the filter value to check if it already exists in the ordered filters array
 * @returns A boolean indicating if the filter already exists in the ordered filters array
 *
 */
function getDoesFilterExistInOrderedFilters(
  orderedFilters: FilterPillComponentData[],
  filterEntityId: string,
  filterValueId: string,
): boolean {
  const relevantOrderedFilterObjects = orderedFilters.filter(({ id }) => id === filterEntityId)
  const relevantOrderedFilters = relevantOrderedFilterObjects.map(({ value }) => value)
  const doesFilterAlreadyExist = !!relevantOrderedFilters.find(existingValue => {
    return typeof existingValue === 'string' ? existingValue === filterValueId : existingValue.id === filterValueId
  })
  return doesFilterAlreadyExist
}

// REDUCER FUNCTIONS - moved here for unit compatibility with unit testing
export const replaceFreetextFilterFunction = (state: FilterReducerState, payload: ToggleFreetextFilterPayload) => {
  const { filterObject, newFilterValue } = payload
  const { id } = filterObject
  const { newIndex, newUrl } = getDeepCopiedData(state)
  if (!getDoesFilterObjectExist(id, state.index)) return state

  const newActiveFilters = [newFilterValue]

  setUrlSearchParamsFromFreetextObject(newUrl, id, newActiveFilters)
  if (!newFilterValue) newUrl.delete(id)
  newIndex[id] = { ...filterObject, activeFilters: newActiveFilters }

  return {
    ...state,
    index: newIndex,
    urlQueryParams: newUrl,
  }
}

export const removeAllFiltersFunction = (state: FilterReducerState, payload: string) => {
  const fieldIdToRemove = payload

  // Create copies of state fields for immutability
  const newIndex = deepCopyFilterObject(state.index)
  const newUrl = new URLSearchParams(state.urlQueryParams)
  let newOrderedFilters = deepCopyFilterObject(state.orderedFilters)

  const doesItemExist = Reflect.has(newIndex, fieldIdToRemove)

  // if a specific field has not been passed, remove all active filters
  if (!fieldIdToRemove) {
    // Reset all active filters to an empty array
    // this makes the default value the new active filter (e.g. for the 'brands' filter when no active filters, default to 'all brands')
    for (const key in newIndex) {
      if (getDoesFilterObjectExist(key, state.index)) {
        const filterObject = newIndex[key]
        newIndex[key] = {
          ...filterObject,
          activeFilters: [],
        }
      }
    }
    // remove all filter values from the API url
    Object.values(newIndex).forEach(filterObject => newUrl.delete(filterObject.id))

    // reset ordered filters (reset filter pills)
    newOrderedFilters = []
  } else {
    // if a specific field has been passed, remove only the active filters for the field specified

    // Reset the active filters to an empty array
    if (doesItemExist) newIndex[fieldIdToRemove].activeFilters = []

    // reset ordered filters (reset filter pills)
    newOrderedFilters = newOrderedFilters.filter(orderedFilter => orderedFilter.parentId !== fieldIdToRemove)

    // remove all filter values from the API url
    newUrl.delete(fieldIdToRemove)
  }
  return {
    ...state,
    index: newIndex,
    urlQueryParams: newUrl,
    orderedFilters: newOrderedFilters,
  }
}

/**
 * used to programmatically select a select filter option (e.g. when pre-selecting filters from the preserved filters)
 *
 * @param state - The filter reducer state
 * @param payload - The reducer action payload, a SelectFilterSelectOptionPayload object
 * @returns The new state representation of the reducer
 */
export const selectFilterSelectOptionReducerFunction = (
  state: FilterReducerState,
  payload: SelectFilterSelectOptionPayload,
): FilterReducerState => {
  const { filterObject, newFilterValues: valuesToSelect } = payload
  const { id } = filterObject
  const { newIndex, newUrl, newOrderedFilters: orderedFilters } = getDeepCopiedData(state)

  if (!getDoesFilterObjectExist(id, state.index)) return state

  let newActiveFilters: IdValuePair[] = []
  let newOrderedFilters = orderedFilters

  // Loop through the values to be selected and add them to the active filters, url & ordered filters
  valuesToSelect.forEach(valueToSelect => {
    // If the default value is selected, remove the filter from the url and ordered filters as they will be included in the default API response
    if (valueToSelect.id === filterObject.defaultValue?.id) {
      newUrl.delete(id)
      newOrderedFilters = orderedFilters.filter(({ parentId }) => parentId !== id)
      return
    } else {
      // If the filter value is not already in the active filters, order filters or url, add it
      if (!newActiveFilters.find(({ id }) => id === valueToSelect.id)) newActiveFilters.push(valueToSelect)
      if (!getDoesFilterExistInOrderedFilters(newOrderedFilters, id, valueToSelect.id))
        newOrderedFilters.push(extractPillComponentData(filterObject, valueToSelect))
      setUrlSearchParamsFromSelectObject(newUrl, id, newActiveFilters)
    }
  })

  //  Add the new active filters to the filter object found in the index field of the reducer state
  newIndex[id] = { ...filterObject, activeFilters: newActiveFilters }

  return {
    ...state,
    index: newIndex,
    urlQueryParams: newUrl,
    orderedFilters: newOrderedFilters,
  }
}

export const selectFilterFreetextOptionReducerFunction = (
  state: FilterReducerState,
  payload: SelectFilterFreetextOptionPayload,
): FilterReducerState => {
  const { filterObject, newFilterValues: valuesToSelect } = payload
  const { id } = filterObject

  const { newIndex, newUrl, newOrderedFilters } = getDeepCopiedData(state)

  if (!getDoesFilterObjectExist(id, state.index)) return state

  const newActiveFilters = valuesToSelect
  // Loop through the values to be selected and add them to the active filters, url & ordered filters
  valuesToSelect.forEach(valueToSelect => {
    if (!getDoesFilterExistInOrderedFilters(newOrderedFilters, id, valueToSelect) && filterObject.isMultiSelect)
      newOrderedFilters.push(extractPillComponentData(filterObject, valueToSelect))
  })

  // Update the url with the new active filters
  newUrl.set(id, newActiveFilters.join(','))

  //  Add the new active filters to the filter object found in the index field of the reducer state
  newIndex[id] = { ...filterObject, activeFilters: newActiveFilters }

  return {
    ...state,
    index: newIndex,
    urlQueryParams: newUrl,
    orderedFilters: newOrderedFilters,
  }
}
